import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  CheckSquare, Plus, Clock, User, Calendar, AlertCircle,
  MoreVertical, Edit, Trash2, FileText, Loader2, Filter,
  Check, Circle, Play, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import SignDocumentButton from '../components/SignDocumentButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priorityColors = {
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-green-500/20 text-green-400 border-green-500/30'
};

const statusIcons = {
  pending: Circle,
  in_progress: Play,
  completed: Check,
  cancelled: XCircle
};

const statusColors = {
  pending: 'text-white/60',
  in_progress: 'text-blue-400',
  completed: 'text-green-400',
  cancelled: 'text-red-400'
};

const TasksPage = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, in_progress: 0, completed: 0, overdue: 0 });
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee_id: '',
    due_date: '',
    priority: 'medium',
    document_id: '',
    is_approval_task: false
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      let url = `${API_URL}/api/tasks?filter_type=${filterType}`;
      if (filterStatus && filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterPriority && filterPriority !== 'all') url += `&priority=${filterPriority}`;
      
      const res = await fetch(url, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
        setStats(data.stats || { total: 0, pending: 0, in_progress: 0, completed: 0, overdue: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filterType, filterStatus, filterPriority]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, not {users: [...]}
        setUsers(Array.isArray(data) ? data : (data.users || []));
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [token]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, not {documents: [...]}
        setDocuments(Array.isArray(data) ? data : (data.documents || []));
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    fetchDocuments();
  }, [fetchTasks, fetchUsers, fetchDocuments]);

  const handleCreateTask = async () => {
    if (!formData.title || !formData.assignee_id) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        assignee_id: formData.assignee_id,
        due_date: formData.due_date || null,
        priority: formData.priority,
        document_id: formData.document_id || null,
        is_approval_task: formData.is_approval_task
      };

      const res = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Task created successfully');
        setCreateDialogOpen(false);
        resetForm();
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (err) {
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!editTask) return;

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        assignee_id: formData.assignee_id,
        due_date: formData.due_date || null,
        priority: formData.priority
      };

      const res = await fetch(`${API_URL}/api/tasks/${editTask.task_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Task updated successfully');
        setEditTask(null);
        resetForm();
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update task');
      }
    } catch (err) {
      toast.error('Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Task marked as ${newStatus.replace('_', ' ')}`);
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const res = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        toast.success('Task deleted');
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete task');
      }
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  const handleApproveDocument = async (documentId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/documents/${documentId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ action })
      });

      if (res.ok) {
        toast.success(`Document ${action}d`);
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || `Failed to ${action} document`);
      }
    } catch (err) {
      toast.error(`Failed to ${action} document`);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assignee_id: '',
      due_date: '',
      priority: 'medium',
      document_id: '',
      is_approval_task: false
    });
  };

  const openEditDialog = (task) => {
    setEditTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      assignee_id: task.assignee_id,
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      priority: task.priority,
      document_id: task.document_id || '',
      is_approval_task: task.is_approval_task
    });
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'No due date';
    const date = new Date(isoString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') return false;
    return new Date(task.due_date) < new Date();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('tasks.title')}
            </h1>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-primary text-black hover:bg-primary/90"
            data-testid="create-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('tasks.createTask')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-black/40 border border-white/10 rounded-sm p-4">
            <p className="text-white/60 text-sm">{t('tasks.total')}</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4">
            <p className="text-white/60 text-sm">{t('tasks.pending')}</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4">
            <p className="text-white/60 text-sm">{t('tasks.inProgress')}</p>
            <p className="text-2xl font-bold text-blue-400">{stats.in_progress}</p>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4">
            <p className="text-white/60 text-sm">{t('tasks.completed')}</p>
            <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4">
            <p className="text-white/60 text-sm">{t('tasks.overdue')}</p>
            <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 bg-black/40 border-white/10 text-white">
              <SelectValue placeholder={t('tasks.filterBy')} />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all" className="text-white">{t('tasks.allTasks')}</SelectItem>
              <SelectItem value="assigned" className="text-white">{t('tasks.assignedToMe')}</SelectItem>
              <SelectItem value="created" className="text-white">{t('tasks.createdByMe')}</SelectItem>
              <SelectItem value="overdue" className="text-white">{t('tasks.overdue')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 bg-black/40 border-white/10 text-white">
              <SelectValue placeholder={t('tasks.status')} />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all" className="text-white">{t('tasks.allStatuses')}</SelectItem>
              <SelectItem value="pending" className="text-white">{t('tasks.pending')}</SelectItem>
              <SelectItem value="in_progress" className="text-white">{t('tasks.inProgress')}</SelectItem>
              <SelectItem value="completed" className="text-white">{t('tasks.completed')}</SelectItem>
              <SelectItem value="cancelled" className="text-white">{t('tasks.cancelled')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-40 bg-black/40 border-white/10 text-white">
              <SelectValue placeholder={t('tasks.priority')} />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all" className="text-white">{t('tasks.allPriorities')}</SelectItem>
              <SelectItem value="high" className="text-white">{t('tasks.high')}</SelectItem>
              <SelectItem value="medium" className="text-white">{t('tasks.medium')}</SelectItem>
              <SelectItem value="low" className="text-white">{t('tasks.low')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Task List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12 bg-black/40 border border-white/10 rounded-sm">
            <CheckSquare className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">{t('tasks.noTasks')}</h3>
            <p className="text-white/40">{t('tasks.noTasksHint')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => {
              const StatusIcon = statusIcons[task.status] || Circle;
              return (
                <div
                  key={task.task_id}
                  className={`p-4 bg-black/40 border rounded-sm hover:bg-white/5 transition-colors ${
                    isOverdue(task) ? 'border-red-500/50' : 'border-white/10'
                  }`}
                  data-testid={`task-${task.task_id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Status Toggle */}
                    <button
                      onClick={() => {
                        const nextStatus = task.status === 'pending' ? 'in_progress' : 
                                          task.status === 'in_progress' ? 'completed' : 'pending';
                        handleStatusChange(task.task_id, nextStatus);
                      }}
                      className={`mt-1 p-1 rounded-sm hover:bg-white/10 ${statusColors[task.status]}`}
                      data-testid={`toggle-status-${task.task_id}`}
                    >
                      <StatusIcon className="w-5 h-5" />
                    </button>

                    {/* Task Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`text-white font-medium ${task.status === 'completed' ? 'line-through opacity-60' : ''}`}>
                          {task.title}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded-sm border ${priorityColors[task.priority]}`}>
                          {task.priority.toUpperCase()}
                        </span>
                        {task.is_approval_task && (
                          <span className="text-xs px-2 py-0.5 rounded-sm bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            APPROVAL
                          </span>
                        )}
                        {isOverdue(task) && (
                          <span className="text-xs px-2 py-0.5 rounded-sm bg-red-500/20 text-red-400">
                            OVERDUE
                          </span>
                        )}
                      </div>
                      
                      {task.description && (
                        <p className="text-white/60 text-sm mt-1 line-clamp-1">{task.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 text-white/40 text-sm flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {task.assignee_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(task.due_date)}
                        </span>
                        {task.document_name && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {task.document_name}
                          </span>
                        )}
                      </div>

                      {/* Sign & Complete document button — visible when task links to a document */}
                      {task.document_id && (
                        <div className="mt-3">
                          <SignDocumentButton
                            doc={{
                              document_id: task.document_id,
                              name: task.document_name || 'Document',
                              file_type: '',
                            }}
                            variant="outline"
                            size="sm"
                            className="border-primary/40 text-primary hover:bg-primary/10"
                            dataTestId={`sign-task-${task.task_id}`}
                            onSigned={() => {
                              // Auto-mark task completed once the user signs (non-approval tasks)
                              if (!task.is_approval_task && task.status !== 'completed') {
                                handleStatusChange(task.task_id, 'completed');
                              }
                            }}
                          >
                            Open & Sign
                          </SignDocumentButton>
                        </div>
                      )}

                      {/* Approval Actions */}
                      {task.is_approval_task && task.approval_status === 'pending' && task.assignee_id === user?.user_id && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={() => handleApproveDocument(task.document_id, 'approve')}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApproveDocument(task.document_id, 'reject')}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-white/60 hover:bg-white/10">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                        <DropdownMenuItem
                          onClick={() => openEditDialog(task)}
                          className="text-white hover:bg-white/5 cursor-pointer"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleStatusChange(task.task_id, 'completed')}
                          className="text-green-400 hover:bg-green-400/10 cursor-pointer"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Mark Complete
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteTask(task.task_id)}
                          className="text-red-400 hover:bg-red-400/10 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Task Dialog */}
        <Dialog open={createDialogOpen || !!editTask} onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditTask(null);
            resetForm();
          }
        }}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <CheckSquare className="w-5 h-5 text-primary" />
                {editTask ? t('tasks.editTask') : t('tasks.createTask')}
              </DialogTitle>
              <DialogDescription className="text-white/60">
                {editTask ? t('tasks.editTask') : t('tasks.description')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Title */}
              <div>
                <Label className="text-white/80">{t('tasks.taskTitle')} *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={t('tasks.titlePlaceholder')}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  data-testid="task-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-white/80">{t('tasks.description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={t('tasks.descriptionPlaceholder')}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  rows={2}
                />
              </div>

              {/* Assignee */}
              <div>
                <Label className="text-white/80">{t('tasks.assignTo')} *</Label>
                <Select
                  value={formData.assignee_id}
                  onValueChange={(v) => setFormData({...formData, assignee_id: v})}
                >
                  <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                    <SelectValue placeholder={t('tasks.selectTeamMember')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {users.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id} className="text-white">
                        {u.name} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Due Date & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">{t('tasks.dueDate')}</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="mt-1 bg-black/50 border-white/10 text-white"
                    data-testid="task-due-date-input"
                  />
                </div>
                <div>
                  <Label className="text-white/80">{t('tasks.priority')}</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(v) => setFormData({...formData, priority: v})}
                  >
                    <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="high" className="text-red-400">{t('tasks.high')}</SelectItem>
                      <SelectItem value="medium" className="text-yellow-400">{t('tasks.medium')}</SelectItem>
                      <SelectItem value="low" className="text-green-400">{t('tasks.low')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Link to Document (for approval workflow) */}
              {!editTask && documents.length > 0 && (
                <div>
                  <Label className="text-white/80 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Link to Document (optional)
                  </Label>
                  <Select
                    value={formData.document_id || 'none'}
                    onValueChange={(v) => setFormData({...formData, document_id: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                      <SelectValue placeholder={t('tasks.selectDocument')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="none" className="text-white">{t('tasks.none')}</SelectItem>
                      {documents.map((doc) => (
                        <SelectItem key={doc.document_id} value={doc.document_id} className="text-white">
                          {doc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Approval Task Toggle */}
              {!editTask && formData.document_id && (
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-sm">
                  <input
                    type="checkbox"
                    id="is_approval_task"
                    checked={formData.is_approval_task}
                    onChange={(e) => setFormData({...formData, is_approval_task: e.target.checked})}
                    className="rounded border-white/20"
                  />
                  <label htmlFor="is_approval_task" className="text-white/80 text-sm cursor-pointer">
                    This is a document approval task
                  </label>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreateDialogOpen(false);
                    setEditTask(null);
                    resetForm();
                  }}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={editTask ? handleUpdateTask : handleCreateTask}
                  disabled={submitting || !formData.title || !formData.assignee_id}
                  className="bg-primary text-black hover:bg-primary/90"
                  data-testid="submit-task-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editTask ? t('tasks.editTask') : t('tasks.createTask')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TasksPage;
